import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate } from 'react-router-dom';
import { assessmentScheduleAPI } from '../../api/assessmentScheduleApi';
import ChipSelect from '../../components/Portal/ChipSelect';
import * as S from './AssessmentScheduleManagement.styles';

// 这里存的是校区名字符串，公开的 Assessment 预约页按这个字符串匹配，不能改成校区 id 或新名称
const ASSESSMENT_CAMPUS_NAMES = ['Victoria Park', 'Claremont', 'Osborne Park'];

const AssessmentScheduleManagement = ({ backPath, userCampusName }) => {
  const auth = useAuth();
  const [assessmentSchedules, setAssessmentSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [newSchedule, setNewSchedule] = useState({
    campusName: '',
    dayOfWeek: 0,
    startTime: '',
    endTime: '',
    isActive: true
  });
  const [scheduleErrors, setScheduleErrors] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [collapsedCampuses, setCollapsedCampuses] = useState(new Set());
  const notificationIdCounter = React.useRef(0);

  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const isSuperAdmin = userGroups.includes('super_admin');
  const hasPortalPermission = userGroups.includes('portal_developer') || userGroups.includes('portal_system_admin') || userGroups.includes('portal_director') || userGroups.includes('portal_manager');
  const isPortalManager = userGroups.includes('portal_manager') && !userGroups.includes('portal_developer') && !userGroups.includes('portal_system_admin') && !userGroups.includes('portal_director');
  const hasPermission = isSuperAdmin || hasPortalPermission;

  // Notification functions
  const showNotification = useCallback((message, type = 'info') => {
    const id = ++notificationIdCounter.current;
    const notification = {
      id,
      message,
      type,
      timestamp: Date.now()
    };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Fetch assessment schedules
  const fetchAssessmentSchedules = useCallback(async () => {
    if (!hasPermission || !auth.user?.id_token) return;

    try {
      setIsLoading(true);
      let schedules = await assessmentScheduleAPI.getAllSchedules(auth.user.id_token);
      // Portal manager can only see their own campus schedules
      if (isPortalManager && userCampusName) {
        schedules = schedules.filter(s => s.campusName === userCampusName);
      }
      setAssessmentSchedules(schedules);
    } catch (error) {
      console.error('Error fetching assessment schedules:', error);
      showNotification('Failed to load assessment schedules', 'warning');
    } finally {
      setIsLoading(false);
    }
  }, [hasPermission, isPortalManager, userCampusName, auth.user?.id_token, showNotification]);

  // Load schedules on component mount
  useEffect(() => {
    fetchAssessmentSchedules();
  }, [fetchAssessmentSchedules]);

  // Initialize collapsed state for all campuses when schedules are loaded
  useEffect(() => {
    if (assessmentSchedules.length > 0) {
      const allCampuses = [...new Set(assessmentSchedules.map(schedule => schedule.campusName))];
      setCollapsedCampuses(new Set(allCampuses));
    }
  }, [assessmentSchedules]);

  // Group schedules by campus
  const groupSchedulesByCampus = () => {
    const grouped = {};
    assessmentSchedules.forEach(schedule => {
      if (!grouped[schedule.campusName]) {
        grouped[schedule.campusName] = [];
      }
      grouped[schedule.campusName].push(schedule);
    });
    
    // Sort schedules within each campus by day of week
    Object.keys(grouped).forEach(campus => {
      grouped[campus].sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    });
    
    // Sort campuses in reverse alphabetical order
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort((a, b) => b.localeCompare(a)) // Reverse alphabetical
      .forEach(campus => {
        sortedGrouped[campus] = grouped[campus];
      });
    
    return sortedGrouped;
  };

  // Check if a campus-day combination already exists
  const isDayTakenForCampus = (campusName, dayOfWeek, excludeId = null) => {
    return assessmentSchedules.some(schedule => 
      schedule.campusName === campusName && 
      schedule.dayOfWeek === dayOfWeek && 
      schedule.id !== excludeId
    );
  };

  // Get available days for a campus
  const getAvailableDaysForCampus = (campusName) => {
    const allDays = [0, 1, 2, 3, 4, 5, 6];
    return allDays.filter(day => !isDayTakenForCampus(campusName, day));
  };

  // Validation function
  const validateSchedule = (schedule, isEditing = false) => {
    const errors = {};
    
    if (!schedule.campusName) {
      errors.campusName = 'Campus name is required';
    }
    
    if (schedule.dayOfWeek === undefined || schedule.dayOfWeek === null) {
      errors.dayOfWeek = 'Day of week is required';
    } else if (!isEditing && isDayTakenForCampus(schedule.campusName, schedule.dayOfWeek)) {
      errors.dayOfWeek = `${getDayName(schedule.dayOfWeek)} is already scheduled for ${schedule.campusName}`;
    } else if (isEditing && isDayTakenForCampus(schedule.campusName, schedule.dayOfWeek, schedule.id)) {
      errors.dayOfWeek = `${getDayName(schedule.dayOfWeek)} is already scheduled for ${schedule.campusName}`;
    }
    
    // Validate start time
    if (!schedule.startTime) {
      errors.startTime = 'Start time is required';
    } else if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(schedule.startTime)) {
      errors.startTime = 'Invalid time format (HH:MM, range: 00:00-23:59)';
    } else {
      const [startHour, startMin] = schedule.startTime.split(':').map(Number);
      if (startHour < 0 || startHour > 23 || startMin < 0 || startMin > 59) {
        errors.startTime = 'Start time must be between 00:00 and 23:59';
      }
    }
    
    // Validate end time
    if (!schedule.endTime) {
      errors.endTime = 'End time is required';
    } else if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(schedule.endTime)) {
      errors.endTime = 'Invalid time format (HH:MM, range: 00:00-23:59)';
    } else {
      const [endHour, endMin] = schedule.endTime.split(':').map(Number);
      if (endHour < 0 || endHour > 23 || endMin < 0 || endMin > 59) {
        errors.endTime = 'End time must be between 00:00 and 23:59';
      }
    }
    
    // Validate start time is before end time
    if (schedule.startTime && schedule.endTime && 
        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(schedule.startTime) && 
        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(schedule.endTime)) {
      
      const [startHour, startMin] = schedule.startTime.split(':').map(Number);
      const [endHour, endMin] = schedule.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      if (startMinutes >= endMinutes) {
        errors.endTime = 'End time must be after start time';
      }
      
      // Check for reasonable time ranges (optional business rule)
      const duration = endMinutes - startMinutes;
      if (duration < 30) {
        errors.endTime = 'Schedule duration must be at least 30 minutes';
      }
    }
    
    return errors;
  };

  // Create new schedule
  const createAssessmentSchedule = async () => {
    const errors = validateSchedule(newSchedule, false);
    if (Object.keys(errors).length > 0) {
      setScheduleErrors(errors);
      return;
    }
    
    setIsLoading(true);
    try {
      await assessmentScheduleAPI.createSchedule(newSchedule, auth.user?.id_token);
      await fetchAssessmentSchedules();
      setNewSchedule({
        campusName: '',
        dayOfWeek: 0,
        startTime: '',
        endTime: '',
        isActive: true
      });
      setScheduleErrors({});
      showNotification('Schedule created successfully', 'success');
      
      // Clear the public API cache
      assessmentScheduleAPI.clearCache();
    } catch (error) {
      console.error('Error creating schedule:', error);
      showNotification('Failed to create schedule', 'warning');
    } finally {
      setIsLoading(false);
    }
  };

  // Update existing schedule
  const updateAssessmentSchedule = async () => {
    if (!editingSchedule) return;
    
    const errors = validateSchedule(editingSchedule, true);
    if (Object.keys(errors).length > 0) {
      setScheduleErrors(errors);
      return;
    }
    
    setIsLoading(true);
    try {
      const { id, created_at, created_by, updated_at, ...updateData } = editingSchedule;
      await assessmentScheduleAPI.updateSchedule(id, updateData, auth.user?.id_token);
      await fetchAssessmentSchedules();
      setEditingSchedule(null);
      setScheduleErrors({});
      showNotification('Schedule updated successfully', 'success');
      
      // Clear the public API cache
      assessmentScheduleAPI.clearCache();
    } catch (error) {
      console.error('Error updating schedule:', error);
      showNotification('Failed to update schedule', 'warning');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete schedule
  const deleteAssessmentSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }
    
    setIsLoading(true);
    try {
      await assessmentScheduleAPI.deleteSchedule(scheduleId, auth.user?.id_token);
      await fetchAssessmentSchedules();
      showNotification('Schedule deleted successfully', 'success');
      
      // Clear the public API cache
      assessmentScheduleAPI.clearCache();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      showNotification('Failed to delete schedule', 'warning');
    } finally {
      setIsLoading(false);
    }
  };

  const getDayName = (dayNumber) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNumber] || 'Unknown';
  };

  // Format time input as user types
  const formatTimeInput = (input) => {
    // Remove all non-digit characters except colon
    let cleaned = input.replace(/[^0-9:]/g, '');
    
    // Auto-add colon after 2 digits
    if (cleaned.length === 2 && !cleaned.includes(':')) {
      cleaned = cleaned + ':';
    }
    
    // Prevent multiple colons
    const colonCount = (cleaned.match(/:/g) || []).length;
    if (colonCount > 1) {
      const firstColonIndex = cleaned.indexOf(':');
      cleaned = cleaned.substring(0, firstColonIndex + 1) + cleaned.substring(firstColonIndex + 1).replace(/:/g, '');
    }
    
    // Limit length
    if (cleaned.length > 5) {
      cleaned = cleaned.substring(0, 5);
    }
    
    return cleaned;
  };

  // Validate and format time on blur
  const validateAndFormatTime = (input) => {
    if (!input) return '';
    
    // Remove any non-digit characters except colon
    let cleaned = input.replace(/[^0-9:]/g, '');
    
    // If no colon, try to format automatically
    if (!cleaned.includes(':')) {
      if (cleaned.length === 3) {
        // Assume format like "930" = "9:30"
        cleaned = cleaned.substring(0, 1) + ':' + cleaned.substring(1);
      } else if (cleaned.length === 4) {
        // Assume format like "0930" = "09:30"
        cleaned = cleaned.substring(0, 2) + ':' + cleaned.substring(2);
      }
    }
    
    // Validate format and range
    const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (timePattern.test(cleaned)) {
      const [hours, minutes] = cleaned.split(':').map(Number);
      
      // Ensure hours are within 0-23 and minutes within 0-59
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        // Pad with leading zero if needed
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    }
    
    // Return original input if format is invalid (let validation handle it)
    return input;
  };

  // Toggle campus collapse state
  const toggleCampusCollapse = (campusName) => {
    setCollapsedCampuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campusName)) {
        newSet.delete(campusName);
      } else {
        newSet.add(campusName);
      }
      return newSet;
    });
  };

  // Wait for auth to initialize
  if (auth.isLoading) {
    return (
      <S.LoadingContainer>
        <S.LoadingSpinner />
        <p>Loading...</p>
      </S.LoadingContainer>
    );
  }

  // Redirect if not authenticated
  if (!auth.isAuthenticated) {
    return <Navigate to={backPath ? '/portal/login' : '/login'} replace />;
  }

  // Redirect if no permission
  if (!hasPermission) {
    return <Navigate to={backPath || '/dashboard'} replace />;
  }

  return (
    <S.Container>
      {/* Notification Container */}
      {notifications.length > 0 && (
        <S.NotificationContainer>
          {notifications.map((notification) => (
            <S.Notification
              key={notification.id}
              type={notification.type}
              onClick={() => removeNotification(notification.id)}
            >
              {notification.message}
              <S.NotificationDismiss>
                Click to dismiss
              </S.NotificationDismiss>
            </S.Notification>
          ))}
        </S.NotificationContainer>
      )}

      <S.Header>
        <h1>Assessment Schedules Management</h1>
        <p>Manage campus booking schedules for assessment appointments</p>
      </S.Header>

      {isLoading && (
        <S.LoadingOverlay>
          <S.LoadingSpinner />
          <p>Processing...</p>
        </S.LoadingOverlay>
      )}

      {/* Create New Schedule Form */}
      <S.Section>
        <S.SectionTitle>Add New Schedule</S.SectionTitle>
        <S.FormGrid>
          <S.FormGroup>
            <S.Label>Campus Name *</S.Label>
            <ChipSelect
              aria-label="Campus"
              value={newSchedule.campusName}
              onChange={(campusName) => {
                setNewSchedule(prev => ({ ...prev, campusName }));
                setScheduleErrors(prev => ({ ...prev, campusName: '' }));
              }}
              options={(isPortalManager && userCampusName ? [userCampusName] : ASSESSMENT_CAMPUS_NAMES)
                .map(name => ({ value: name, label: name }))}
            />
            {scheduleErrors.campusName && <S.ErrorMessage>{scheduleErrors.campusName}</S.ErrorMessage>}
          </S.FormGroup>
          
          <S.FormGroup>
            <S.Label>Day of Week *</S.Label>
            {!newSchedule.campusName ? (
              <S.ScheduleFieldNote>Select a campus first</S.ScheduleFieldNote>
            ) : getAvailableDaysForCampus(newSchedule.campusName).length === 0 ? (
              <S.ScheduleFieldNote>No available days for this campus</S.ScheduleFieldNote>
            ) : (
              <ChipSelect
                aria-label="Day of week"
                value={newSchedule.dayOfWeek}
                onChange={(dayOfWeek) => {
                  setNewSchedule(prev => ({ ...prev, dayOfWeek: Number(dayOfWeek) }));
                  setScheduleErrors(prev => ({ ...prev, dayOfWeek: '' }));
                }}
                options={getAvailableDaysForCampus(newSchedule.campusName)
                  .map(dayNum => ({ value: dayNum, label: getDayName(dayNum) }))}
              />
            )}
            {scheduleErrors.dayOfWeek && <S.ErrorMessage>{scheduleErrors.dayOfWeek}</S.ErrorMessage>}
          </S.FormGroup>
          
          <S.FormGroup>
            <S.Label>Start Time (HH:MM) *</S.Label>
            <S.Input
              type="text"
              placeholder="e.g., 09:00"
              maxLength="5"
              value={newSchedule.startTime}
              onChange={(e) => {
                const value = formatTimeInput(e.target.value);
                setNewSchedule(prev => ({ ...prev, startTime: value }));
                setScheduleErrors(prev => ({ ...prev, startTime: '' }));
              }}
              onBlur={(e) => {
                const value = validateAndFormatTime(e.target.value);
                setNewSchedule(prev => ({ ...prev, startTime: value }));
              }}
            />
            {scheduleErrors.startTime && <S.ErrorMessage>{scheduleErrors.startTime}</S.ErrorMessage>}
          </S.FormGroup>
          
          <S.FormGroup>
            <S.Label>End Time (HH:MM) *</S.Label>
            <S.Input
              type="text"
              placeholder="e.g., 17:00"
              maxLength="5"
              value={newSchedule.endTime}
              onChange={(e) => {
                const value = formatTimeInput(e.target.value);
                setNewSchedule(prev => ({ ...prev, endTime: value }));
                setScheduleErrors(prev => ({ ...prev, endTime: '' }));
              }}
              onBlur={(e) => {
                const value = validateAndFormatTime(e.target.value);
                setNewSchedule(prev => ({ ...prev, endTime: value }));
              }}
            />
            {scheduleErrors.endTime && <S.ErrorMessage>{scheduleErrors.endTime}</S.ErrorMessage>}
          </S.FormGroup>
          
          <S.FormGroup style={{ gridColumn: 'span 2' }}>
            <S.CheckboxContainer>
              <input
                type="checkbox"
                id="active-checkbox"
                checked={newSchedule.isActive}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              <S.Label htmlFor="active-checkbox">Active (Enable this time slot for bookings)</S.Label>
            </S.CheckboxContainer>
          </S.FormGroup>
        </S.FormGrid>
        
        <S.ButtonContainer>
          <S.Button onClick={createAssessmentSchedule} disabled={isLoading}>
            Create Schedule
          </S.Button>
        </S.ButtonContainer>
      </S.Section>

      {/* Existing Schedules */}
      <S.Section>
        <S.SectionTitle>Existing Schedules ({assessmentSchedules.length})</S.SectionTitle>
        
        {assessmentSchedules.length > 0 ? (
          <>
            {Object.entries(groupSchedulesByCampus()).map(([campusName, schedules]) => {
              const isCollapsed = collapsedCampuses.has(campusName);
              return (
                <div key={campusName} style={{ marginBottom: '2rem' }}>
                  <S.CampusHeader onClick={() => toggleCampusCollapse(campusName)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <S.CollapseIcon collapsed={isCollapsed}>
                        ▼
                      </S.CollapseIcon>
                      <h3>{campusName} Campus</h3>
                    </div>
                    <span>({schedules.length} schedule{schedules.length !== 1 ? 's' : ''})</span>
                  </S.CampusHeader>
                {!isCollapsed && (
                  <S.TableContainer>
                    <S.Table>
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map((schedule) => (
                        <tr key={schedule.id}>
                          {editingSchedule?.id === schedule.id ? (
                            <>
                              <td>
                                {/* 本行原来的星期也算可选 —— 它被这一行自己占着，不在「空闲」列表里 */}
                                <ChipSelect
                                  size="sm"
                                  aria-label="Day of week"
                                  value={editingSchedule.dayOfWeek}
                                  onChange={(dayOfWeek) => setEditingSchedule(prev => ({ ...prev, dayOfWeek: Number(dayOfWeek) }))}
                                  options={[...new Set([schedule.dayOfWeek, ...getAvailableDaysForCampus(editingSchedule.campusName)])]
                                    .sort((a, b) => a - b)
                                    .map(dayNum => ({ value: dayNum, label: getDayName(dayNum).slice(0, 3) }))}
                                />
                              </td>
                              <td>
                                <S.Input
                                  type="text"
                                  maxLength="5"
                                  value={editingSchedule.startTime}
                                  onChange={(e) => {
                                    const value = formatTimeInput(e.target.value);
                                    setEditingSchedule(prev => ({ ...prev, startTime: value }));
                                  }}
                                  onBlur={(e) => {
                                    const value = validateAndFormatTime(e.target.value);
                                    setEditingSchedule(prev => ({ ...prev, startTime: value }));
                                  }}
                                />
                              </td>
                              <td>
                                <S.Input
                                  type="text"
                                  maxLength="5"
                                  value={editingSchedule.endTime}
                                  onChange={(e) => {
                                    const value = formatTimeInput(e.target.value);
                                    setEditingSchedule(prev => ({ ...prev, endTime: value }));
                                  }}
                                  onBlur={(e) => {
                                    const value = validateAndFormatTime(e.target.value);
                                    setEditingSchedule(prev => ({ ...prev, endTime: value }));
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={editingSchedule.isActive}
                                  onChange={(e) => setEditingSchedule(prev => ({ ...prev, isActive: e.target.checked }))}
                                />
                              </td>
                              <td>
                                <S.ActionButtons>
                                  <S.Button variant="success" onClick={updateAssessmentSchedule} disabled={isLoading}>
                                    Save
                                  </S.Button>
                                  <S.Button
                                    variant="secondary"
                                    onClick={() => {
                                      setEditingSchedule(null);
                                      setScheduleErrors({});
                                    }}
                                  >
                                    Cancel
                                  </S.Button>
                                </S.ActionButtons>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{getDayName(schedule.dayOfWeek)}</td>
                              <td>{schedule.startTime}</td>
                              <td>{schedule.endTime}</td>
                              <td>
                                <S.StatusBadge active={schedule.isActive}>
                                  {schedule.isActive ? 'Active' : 'Inactive'}
                                </S.StatusBadge>
                              </td>
                              <td>
                                <S.ActionButtons>
                                  <S.Button
                                    variant="primary"
                                    onClick={() => setEditingSchedule(schedule)}
                                    disabled={isLoading}
                                  >
                                    Edit
                                  </S.Button>
                                  <S.Button
                                    variant="danger"
                                    onClick={() => deleteAssessmentSchedule(schedule.id)}
                                    disabled={isLoading}
                                  >
                                    Delete
                                  </S.Button>
                                </S.ActionButtons>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    </S.Table>
                  </S.TableContainer>
                )}
                </div>
              );
            })}
            
            {scheduleErrors.startTime || scheduleErrors.endTime ? (
              <S.ErrorMessage style={{ marginTop: '10px', textAlign: 'center' }}>
                {scheduleErrors.startTime || scheduleErrors.endTime}
              </S.ErrorMessage>
            ) : null}
          </>
        ) : (
          <S.EmptyState>
            <h3>No Assessment Schedules Yet</h3>
            <p>Create your first assessment schedule to enable online bookings.</p>
          </S.EmptyState>
        )}
      </S.Section>
    </S.Container>
  );
};

export default AssessmentScheduleManagement;